/**
 * Customer Detail Page
 *
 * Individual customer analytics with purchase history,
 * trends, growth metrics, and category breakdown.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useCustomerDetails, useCurrentPeriod } from "../hooks/useSalesData";
import { PeriodSelector } from "../components/sales/PeriodSelector";
import { CategoryChart } from "../components/sales/CategoryChart";
import { TrendChart } from "../components/sales/TrendChart";
import { MonthlySalesTrendChart } from "../components/sales/MonthlySalesTrendChart";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { DollarSign, FileText, TrendingUp, Users, ArrowLeft, InfoIcon } from "lucide-react";
import { formatThaiDate } from "@/shared/lib/rfm-translations";

export default function CustomerDetail() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentPeriod = useCurrentPeriod();

  // Read period from URL query parameter, fallback to current period
  const periodFromUrl = searchParams.get("period");
  const initialPeriod = periodFromUrl || currentPeriod;

  const [selectedPeriod, setSelectedPeriod] = useState(initialPeriod);

  // Update selected period when URL parameter changes
  useEffect(() => {
    if (periodFromUrl) {
      setSelectedPeriod(periodFromUrl);
    }
  }, [periodFromUrl]);

  const { data, isLoading, error } = useCustomerDetails(customerId, {
    period: selectedPeriod,
    includeMonthlyTrend: true
  });

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-red-600">
              Error loading customer details: {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Customer not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Format period for display
  const periodDisplay = `${data.period.month}/${data.period.year}`;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header with Back Button */}
      <div className="space-y-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">{data.customerName}</h1>
            <p className="text-muted-foreground">
              Customer ID: {data.customerId}
            </p>
          </div>
          <PeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
        </div>
      </div>

      {/* No Data Alert Banner */}
      {!data.hasData && (
        <Alert className="border-orange-200 bg-orange-50">
          <InfoIcon className="h-4 w-4 text-orange-600" />
          <AlertTitle className="text-orange-900">No Activity in This Period</AlertTitle>
          <AlertDescription className="text-orange-800">
            This customer had no purchases in <strong>{periodDisplay}</strong>.
            {data.monthlyTrend && data.monthlyTrend.months.length > 0 && (
              <span> Check the monthly trend chart below to see when they were last active, or use the period selector above to view historical data.</span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ฿{data.currentPeriod.revenue.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.growth.revenueChangePercentage > 0 ? '+' : ''}
              {data.growth.revenueChangePercentage.toFixed(1)}% from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.currentPeriod.projectCount}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.growth.projectChangeCount > 0 ? '+' : ''}
              {data.growth.projectChangeCount} from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Order Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ฿{Math.round(data.currentPeriod.avgOrderValue).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.growth.avgOrderValueChangePercentage > 0 ? '+' : ''}
              {data.growth.avgOrderValueChangePercentage.toFixed(1)}% change
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Salespersons</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.currentPeriod.salespersonsCount}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.currentPeriod.salespersonsCount === 1 ? 'Single salesperson' : 'Multiple salespersons'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Period Comparison Card */}
      <TrendChart
        comparison={{
          previousRevenue: data.previousPeriod.revenue,
          revenueChangeAmount: data.growth.revenueChangeAmount,
          revenueChangePercentage: data.growth.revenueChangePercentage,
          projectChangeCount: data.growth.projectChangeCount,
          previousProjectCount: data.previousPeriod.projectCount
        }}
        title="Period-over-Period Performance"
        description="Revenue and project comparison"
      />

      {/* Category Distribution Chart */}
      <CategoryChart
        data={data.categoryDistribution}
        title="Spending by Category"
        description="Product category distribution"
      />

      {/* Monthly Trend Chart */}
      {data.monthlyTrend && data.monthlyTrend.months.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>10-Month Sales Trend</CardTitle>
            <CardDescription>
              Revenue and invoice trends over the last 10 months
            </CardDescription>
          </CardHeader>
          <CardContent>
            <MonthlySalesTrendChart
              data={data.monthlyTrend.months}
              title=""
              description=""
            />
            <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-medium">Total Revenue (10 months)</p>
                  <p className="text-lg font-bold text-foreground">
                    ฿{data.monthlyTrend.totalRevenue.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="font-medium">Average Growth Rate</p>
                  <p className={`text-lg font-bold ${
                    data.monthlyTrend.averageGrowth > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {data.monthlyTrend.averageGrowth > 0 ? '+' : ''}
                    {data.monthlyTrend.averageGrowth.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Categories */}
      <Card>
        <CardHeader>
          <CardTitle>Top Categories</CardTitle>
          <CardDescription>
            Top 5 product categories by spending
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.topCategories.map((category, index) => (
              <div key={category.category} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="outline">{index + 1}</Badge>
                  <div>
                    <p className="font-medium">{category.categoryName}</p>
                    <p className="text-sm text-muted-foreground">
                      {category.percentage.toFixed(1)}% of total
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold">฿{category.revenue.toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Project History Table */}
      <Card>
        <CardHeader>
          <CardTitle>Project History</CardTitle>
          <CardDescription>
            All projects for this customer in the selected period
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project ID</TableHead>
                  <TableHead>Salesperson</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead>Paid Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.projectHistory.map((project) => (
                  <TableRow key={project.projectId}>
                    <TableCell className="font-mono text-sm">
                      {project.projectId}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        className="p-0 h-auto font-normal"
                        onClick={() => navigate(`/analytics/salespersons/${project.salespersonId}`)}
                      >
                        {project.salespersonName}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{project.mainCategory}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      ฿{project.revenue.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatThaiDate(project.paidDate)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={project.status === 'PAID' ? 'default' : 'secondary'}
                      >
                        {project.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
